import React, { useState } from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { Box } from '@mui/material';
import AppLayout from '@/components/layout/AppLayout';
import S3ManagerPage from '@/pages/S3ManagerPage';
import UserManagementPage from '@/pages/UserManagementPage';
import PWAUpdatePrompt from '@/components/pwa/PWAUpdatePrompt';
import { useAuth } from '@/hooks/useAuth';
import './index.css';

// 新バージョンデプロイ時の古いチャンク取得失敗を検知して自動リロード
window.addEventListener('vite:preloadError', () => {
  window.location.reload();
});

// モックモードかどうか
const isMockMode = import.meta.env.VITE_APP_MOCK_MODE === 'true';

// ページ種別
type PageType = 'main' | 'userManagement';

// MUIテーマ設定
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
  typography: {
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Noto Sans JP"',
    ].join(','),
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
        },
      },
    },
  },
});

// メインアプリコンポーネント
const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<PageType>('main');
  const { loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        読み込み中...
      </Box>
    );
  }

  // ユーザー管理ページ
  if (currentPage === 'userManagement') {
    return (
      <AppLayout
        onNavigateToTop={() => setCurrentPage('main')}
      >
        <UserManagementPage onComplete={() => setCurrentPage('main')} />
      </AppLayout>
    );
  }

  // メインページ（S3ファイル管理）
  return (
    <AppLayout
      onNavigateToUserManagement={() => setCurrentPage('userManagement')}
      onNavigateToTop={() => setCurrentPage('main')}
    >
      <S3ManagerPage />
    </AppLayout>
  );
};

// モックモードの場合は認証なしで表示
if (isMockMode) {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
        <PWAUpdatePrompt />
      </ThemeProvider>
    </React.StrictMode>
  );
} else {
  // 本番モード: Cognito認証を使用
  import('@aws-amplify/ui-react').then(({ Authenticator }) => {
    import('@/components/auth/AuthProvider').then(({ default: AuthProvider }) => {
      ReactDOM.createRoot(document.getElementById('root')!).render(
        <React.StrictMode>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <Authenticator.Provider>
              <AuthProvider>
                <App />
                <PWAUpdatePrompt />
              </AuthProvider>
            </Authenticator.Provider>
          </ThemeProvider>
        </React.StrictMode>
      );
    });
  });
}
